import { observable, action } from "mobx";

/**
 * Handles most of the common drag & drop behaviour like reordering, selection, multi selection via SHIFT or CMD
 * keys.
 * You must create onClick, onItemDragBegin, onItemDragEnd and reorder methods in your main store and delegate to
 * the matching/corresponding methods here.
 * @todo This currently doesn't support group of items, e.g floors + panoramas in the immersive3dTour project.
 */
class DnDStore {
  /**
   * Field name for id
   *
   * By default id
   * Please note : This is only for the side of the items
   * At the side of the id to compare, it will be always id
   * @property {string}
   */
  fieldNameForId = "id";

  constructor(fieldNameForId = null) {
    if (fieldNameForId !== null) {
      this.fieldNameForId = fieldNameForId;
    }
  }

  /**
   * We'll track when an item is being dragged. This should be passed to DnDItem component in order to apply
   * the correct classNames when needed.
   */
  @observable draggingItemId = null;

  /**
   * We'll use this prop in order to figure out the items multi select when holding down the SHIFT key.
   * This is intended for internal use.
   */
  @observable lastSelectedItemId = null;

  /**
   * Mark given item as selected while unselecting all other items.
   * @param {Array} items
   * @param {any} itemId
   * @returns {Array} items
   */
  @action
  setItemSelectedGreedy(items, itemId) {
    items.forEach(item => {
      item.setIsSelected(item[this.fieldNameForId] === itemId);
    });

    this.lastSelectedItemId = itemId;

    return items;
  }

  /**
   * Mark given item as selected, and nothing more.
   * @param {Array} items
   * @param {any} itemId
   * @returns {Array} items
   */
  @action
  setItemSelected(items, itemId) {
    items.some(item => {
      if (item[this.fieldNameForId] === itemId) {
        item.setIsSelected(true);
        // break
        return true;
      }

      return false;
    });

    this.lastSelectedItemId = itemId;

    return items;
  }

  /**
   * @param {Array} items
   * @param {any} itemId
   * @returns {Array} items
   */
  @action
  toggleItemSelected(items, itemId) {
    items.some(item => {
      if (item[this.fieldNameForId] === itemId) {
        // toggle isSelected
        item.setIsSelected(!item.isSelected);
        this.lastSelectedItemId = item.isSelected ? itemId : null;

        // break
        return true;
      }

      return false;
    });

    return items;
  }

  /**
   * Handle selecting a item when the user is holding the SHIFT key (span multiple items)
   * @todo This should handle multi item groups spanning as well, e.g like floors and panos in the Immersive3dTour
   * project.
   * @param {Array} items
   * @param {any} itemId
   * @returns {Array} items
   */
  @action
  spanItemSelection(items, itemId) {
    // If there are no item selected already then count this action as a single selection.
    if (!this.lastSelectedItemId) {
      return this.setItemSelected(items, itemId);
    }

    // If there's already a item selected but it's the same as the one we are currently trying to
    // select, then count it as a single item selection.
    if (this.lastSelectedItemId === itemId) {
      return this.setItemSelected(items, itemId);
    }

    // We'll mark selected all items in between the two items.
    const lastItem = items.find(p => p[this.fieldNameForId] === this.lastSelectedItemId);
    const currentItem = items.find(p => p[this.fieldNameForId] === itemId);
    const lastIndex = items.indexOf(lastItem);
    const currentIndex = items.indexOf(currentItem);

    let indexFrom;
    let indexTo;

    // span from left to right?
    if (lastIndex < currentIndex) {
      // add one so we don't iterate over it in the next loop.
      indexFrom = lastIndex + 1;
      indexTo = currentIndex;
    } else {
      // span from right to left;
      indexFrom = currentIndex;
      // remove one so we don't iterate over it in the next loop.
      indexTo = lastIndex - 1;
    }

    // will be used to know if we should perform a selection or deselection.
    let selectedValue = false;

    for (let i = indexFrom; i <= indexTo; i += 1) {
      const item = items[i];

      if (!item.isSelected) {
        selectedValue = true;
        break;
      }
    }

    for (let i = indexFrom; i <= indexTo; i += 1) {
      const item = items[i];
      item.setIsSelected(selectedValue);
    }

    return items;
    // Note that we don't set the itemId as the lastSelected item id. This is on purpose since
    // it offers the best UX. For example, User clicks item A, holds shift key and clicks Item D,
    // it means items from A thru D will be selected, but D won't be tracked as the last clicked item even
    // if it technically is.
  }

  /**
   * @param {Array} items
   * @returns {Array} items
   */
  @action
  unselectAllItems(items) {
    items.forEach(item => item.setIsSelected(false));
    this.lastSelectedItemId = null;

    return items;
  }

  /**
   * @param {Array} items the full list of items
   * @param {object} item The item bound at the DndItem onClick handler. shape of {id, type, accepts, etc...}
   * @param {MouseEvent} mouseEvent The click event
   */
  onClick(items, itemId, mouseEvent) {
    // metKey is the CMD in Mac. Also it's the Windows button on Windows, but that button is not used for multi select,
    // we use CTRL instead. Also applies for keyboard in Linux.
    const isMetaKey = mouseEvent.metaKey || mouseEvent.ctrlKey;
    const isShiftKey = mouseEvent.shiftKey;

    // We'll select panoramas taking into account if the meta key (CMD / CTRL) or the shift key are being pressed.
    const bothKeysPressed = isMetaKey && isShiftKey;
    const noneKeysPressed = !isMetaKey && !isShiftKey;

    if (bothKeysPressed || noneKeysPressed) {
      return this.setItemSelectedGreedy(items, itemId);
    }

    // If the CMD / CTRL key is pressed, then toggle the selection of the pano.
    if (isMetaKey) {
      return this.toggleItemSelected(items, itemId);
    }

    if (isShiftKey) {
      return this.spanItemSelection(items, itemId);
    }

    return items;
  }

  /**
   * @param {Array} items
   * @param {object} sourceItem same shape as the "item" used at DnDItem
   */
  onItemDragBegin(items, itemId) {
    let itemsCopy = items;

    // Since the "click" event won't fire when an item is being dragged (not a bug, btw), then we will set it ourselves.
    for (let i = 0; itemsCopy.length < i; i += 1) {
      const itemInLoop = itemsCopy[i];
      // We'll only act on the matching item.
      if (itemInLoop[this.fieldNameForId] === itemId) {
        // if the item was selected already, then do nothing
        if (itemInLoop.isSelected) {
          // break
          break;
        }

        // item isSelected is false, then let's select it greedy.
        itemsCopy = this.setItemSelectedGreedy(itemsCopy, itemId);
        // break
        break;
      }
    }

    // Let's track that the panorama is dragging.
    this.draggingItemId = itemId;

    return itemsCopy;
  }

  /**
   * This method doesn't do much other than run some sanity checks and unselecting selected items after being dropped.
   * You may want to create your own onDragEnd function if you need extra functionality.
   * @param {Array} items
   * @param {object} sourceItem same shape as the "item" used at DnDItem
   * @param {object} targetItem same shape as the "item" used at DnDItem
   * @returns {Array} items
   */
  onItemDragEnd(items, sourceItem, targetItem) {
    // Let's un-track the panorama that was being dragged.
    this.draggingItemId = null;

    if (!(sourceItem && targetItem)) {
      return items;
    }

    // make sure both item are of the same type.
    if (sourceItem.type !== targetItem.type) {
      throw new Error(
        `item types are incompatible: {$sourceItem.type} vs ${targetItem.type}. You may want to create your own onItemDragEnd handler`,
      );
    }

    // Once sorting is done, then mark all as unselected.
    return this.unselectAllItems(items);
  }

  /**
   * @param {Array} items
   * @param {any} sourceId
   * @param {any} targetId
   * @returns {Array} items
   */
  @action
  reorder(items, sourceId, targetId) {
    const sourceItem = items.find(item => item[this.fieldNameForId] === sourceId);
    const targetItem = items.find(item => item[this.fieldNameForId] === targetId);

    // now get a list of all selected items, reversed so we can later on insert from last to first at the target index.
    // If sourceItem wasn't selected, then treat it as the selected item but "greedy", in other words, unselect
    // the rest of the previously selected items.

    let selectedItems = [];
    let newItems = items;

    if (sourceItem.isSelected) {
      selectedItems = items.filter(item => item.isSelected).reverse();
    } else {
      // set as isSelected but greedy. It means that any previously selected item will be set as unselected.
      newItems = this.setItemSelectedGreedy(items, sourceId);
      selectedItems = [sourceItem];
    }

    // if target item is selected then do not nothing since we only want to swap with non selected items.
    // Note, this check must be done after computing if source item was selected or not. If source item wasn't
    // selected, then we'll unselect any previous item and mark the source item as the only selected item.
    // this means that the target item could be unselected on the process; and this should happen before reaching
    // the following check.
    if (targetItem.isSelected) {
      return items;
    }

    // find indexes
    const targetIndex = items.indexOf(targetItem);

    // get a list of non selected items.
    newItems = newItems.filter(item => !item.isSelected);

    // now insert the selected items at the index of the target item.
    selectedItems.forEach(selectedItem => {
      newItems.splice(targetIndex, 0, selectedItem);
    });

    return newItems;
  }

  /**
   * Implement this function in your main store.
   * In your main component, add a document.body.addEventListener("click", yourStore.bodyClickListener);
   *  Remove the listener on the component unmount event.
   *
   * @param {MouseEvent} event the "click" event
   * @param {Array} items
   * @returns {Array} items
   */
  bodyClickListener(event, items, ignoreElementsWithSelectors = []) {
    ignoreElementsWithSelectors.push(".draggable");

    if (!event.target.closest(ignoreElementsWithSelectors.join(","))) {
      return this.unselectAllItems(items);
    }

    return items;
  }
}

export default DnDStore;
